import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import BrandLogo from './components/ui/BrandLogo';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import { useLocaleStore } from './stores/localeStore';
import { useLocalizedDocument } from './i18n/useLocalizedDocument';
import LandingPage from './components/public/LandingPage';
import { HowItWorksPage, NotFoundPage, SafetyPage } from './components/public/InfoPages';
import { PublicProfilePage, PublicStatusPage } from './components/public/PublicDetailPages';
import RegistrationCelebration from './components/auth/RegistrationCelebration';
import { PeoplePage, PlacePage, PlacesPage, StoriesPage } from './components/public/DiscoveryPages';
import { HASHTAG_NAVIGATION_EVENT } from './components/hashtags/HashtagText';
import { formatDocumentTitle, useDocumentTitle } from './i18n/documentTitle';

const MapView = React.lazy(() => import('./components/map/MapView'));
const DeveloperPage = React.lazy(() => import('./components/public/DeveloperPage'));
const ObserverPage = React.lazy(() => import('./components/observer/ObserverPage'));

const AppLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="relative flex min-h-screen-safe items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-950">
    <div aria-hidden="true" className="absolute h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />
    <div className="relative text-center">
      <BrandLogo iconOnly className="mx-auto mb-5 h-16 w-16 drop-shadow-[0_12px_22px_rgba(7,82,213,0.22)]" />
      <div className="mx-auto mb-3 h-1 w-28 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10"><div className="h-full w-1/2 animate-pulse rounded-full bg-gradient-to-r from-cyan-400 to-violet-500" /></div>
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{message}</p>
    </div>
  </div>
);

const MapRoute: React.FC<{ children: React.ReactNode; ready: boolean }> = ({ children, ready }) => {
  const { isLoading } = useAuthStore();

  if (!ready || isLoading) {
    return <AppLoader message="Opening your map…" />;
  }

  return <>{children}</>;
};

const MemberRoute: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  return isAuthenticated
    ? null
    : <Navigate to="/register" replace state={{ returnTo: location.pathname, intent: 'use this member feature' }} />;
};

const ProtectedPageRoute: React.FC<{ ready: boolean; children: React.ReactNode }> = ({ ready, children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  if (!ready || isLoading) return <AppLoader message="Checking access…" />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ returnTo: location.pathname }} />;
  return <>{children}</>;
};

const HashtagNavigationListener: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleHashtagNavigation = (event: Event) => {
      const hashtag = (event as CustomEvent<{ hashtag?: string }>).detail?.hashtag;
      if (hashtag) navigate(`/app/hashtags?tag=${encodeURIComponent(hashtag)}`);
    };

    window.addEventListener(HASHTAG_NAVIGATION_EVENT, handleHashtagNavigation);
    return () => window.removeEventListener(HASHTAG_NAVIGATION_EVENT, handleHashtagNavigation);
  }, [navigate]);

  return null;
};

const StaticDocumentTitle: React.FC = () => {
  const location = useLocation();
  const locale = useLocaleStore((state) => state.locale);
  const title = location.pathname === '/login'
    ? 'Sign in'
    : location.pathname === '/register'
      ? 'Join mapbl'
      : null;

  useDocumentTitle(title ? formatDocumentTitle(title, locale) : null);
  return null;
};

// Auth layout component with redirect logic
const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, registrationCelebration } = useAuthStore();
  const location = useLocation();

  // If user is authenticated, redirect to map
  if (!isLoading && isAuthenticated) {
    const state = location.state as { returnTo?: string } | null;
    const returnTo = registrationCelebration
      ? '/app'
      : state?.returnTo?.startsWith('/app') || state?.returnTo === '/observer' ? state.returnTo : '/app';
    return <Navigate to={returnTo} replace />;
  }

  return (
    <div className="min-h-screen-safe bg-gray-50 dark:bg-gray-900">
      {children}
    </div>
  );
};

// Map layout component
const MapLayout: React.FC = () => {
  return (
    <div className="h-screen-safe w-full relative">
      <React.Suspense fallback={<AppLoader message="Opening the live map…" />}>
        <MapView />
      </React.Suspense>
      <Outlet />
    </div>
  );
};

const MEMBER_SUBROUTES = [
  'messages',
  'profile',
  'notifications',
  'favorites',
  'my-statuses',
  'settings',
  'my-location',
];

const App: React.FC = () => {
  const { checkAuth } = useAuthStore();
  const { isDark } = useThemeStore();
  const { locale, initialize: initializeLocale, setLocale } = useLocaleStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const userLocale = useAuthStore((state) => state.user?.locale);

  useLocalizedDocument(locale);

  useEffect(() => {
    const initializeApp = async () => {
      await initializeLocale();
      await checkAuth();
      setIsInitialized(true);
    };

    initializeApp();
  }, [checkAuth, initializeLocale]);

  useEffect(() => {
    if (userLocale) setLocale(userLocale, true);
  }, [userLocale, setLocale]);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const basename = process.env.REACT_APP_BASENAME || '/';

  return (
    <Router basename={basename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <HashtagNavigationListener />
      <StaticDocumentTitle />
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/safety" element={<SafetyPage />} />
          <Route
            path="/developer"
            element={
              <React.Suspense fallback={<AppLoader message="Loading API reference…" />}>
                <DeveloperPage />
              </React.Suspense>
            }
          />
          <Route path="/people/:userId" element={<PublicProfilePage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/statuses/:statusId" element={<PublicStatusPage />} />
          <Route path="/stories" element={<StoriesPage />} />
          <Route path="/places" element={<PlacesPage />} />
          <Route path="/places/:country/:city" element={<PlacePage />} />
          <Route
            path="/login"
            element={
              <AuthLayout>
                <LoginForm />
              </AuthLayout>
            }
          />
          <Route
            path="/register"
            element={
              <AuthLayout>
                <RegisterForm />
              </AuthLayout>
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="/observer"
            element={
              <ProtectedPageRoute ready={isInitialized}>
                <React.Suspense fallback={<AppLoader message="Opening Observer CRM…" />}>
                  <ObserverPage />
                </React.Suspense>
              </ProtectedPageRoute>
            }
          />

          {/* The map is public; account-only subroutes redirect guests to registration. */}
          <Route
            path="/app"
            element={
              <MapRoute ready={isInitialized}>
                <MapLayout />
              </MapRoute>
            }
          >
            <Route index element={null} />
            {MEMBER_SUBROUTES.map((path) => <Route key={path} path={path} element={<MemberRoute />} />)}
            <Route path="pulse" element={null} />
            <Route path="nearby-statuses" element={null} />
            <Route path="hashtags" element={null} />
            <Route path="about" element={null} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <RegistrationCelebration />
      </div>
    </Router>
  );
};

export default App;
